import {
  addDays,
  differenceInCalendarDays,
  getISODay,
  getOverlappingDaysInIntervals,
  isAfter,
  isBefore,
  isEqual,
  isWithinInterval,
} from 'date-fns';

import { ScheduleItem, ScheduleItemInput } from '../../../graphql/components';
import { DateType } from '../../../types';
import { FormValues } from '../types';

export const cloneSchedule = (newData: FormValues, oldData: FormValues = {}) => {
  if (!newData.cloneScheduleId) {
    return false;
  }

  if (!oldData.cloneScheduleId) {
    return true;
  }

  if (newData.cloneScheduleId !== oldData.cloneScheduleId) {
    return true;
  }

  const startDateTimeEqual =
    newData.schedulingStartDateTime && oldData.schedulingStartDateTime
      ? isEqual(newData.schedulingStartDateTime, oldData.schedulingStartDateTime)
      : true;
  const endDateTimeEqual =
    newData.schedulingEndDateTime && oldData.schedulingEndDateTime
      ? isEqual(newData.schedulingEndDateTime, oldData.schedulingEndDateTime)
      : true;

  if (!startDateTimeEqual || !endDateTimeEqual) {
    return true;
  }

  return false;
};

/**
 * Returns an array of items with new daterange by finding the biggest overlap between old and new schedule ranges.
 *
 * Days of the week match, but dates are changed (item scheduled for Monday is rescheduled to another Monday, ...).
 * Items with dates not fitting into new range are omitted.
 */
export const shiftItemsDateRange = (items: ScheduleItem[], scheduleStartDate: DateType, scheduleEndDate: DateType) => {
  if (items.length === 0) {
    return [];
  }

  const scheduleStart = new Date(scheduleStartDate);
  const scheduleEnd = new Date(scheduleEndDate);
  const scheduleEndExtended = addDays(scheduleEnd, 1); // elements could exceed to +1 day, but no new shift should have started on that day

  // Find items range
  let oldStartDate: Date = new Date(items[0].dateTimeRange.startDateTime);
  let oldEndDate: Date = new Date(items[0].dateTimeRange.endDateTime);

  items.forEach(({ dateTimeRange: { startDateTime, endDateTime } }) => {
    const startDate = new Date(startDateTime);
    const endDate = new Date(endDateTime);

    if (isBefore(startDate, oldStartDate)) {
      oldStartDate = startDate;
    }

    if (isAfter(endDate, oldEndDate)) {
      oldEndDate = endDate;
    }
  });

  // Find number of days to shift
  const diff = differenceInCalendarDays(scheduleStart, oldStartDate);
  const oldDay = getISODay(oldStartDate);
  const newDay = getISODay(scheduleStart);
  const diffWeekDays = oldDay === newDay ? 0 : oldDay > newDay ? oldDay - newDay : 7 + oldDay - newDay;

  const shiftForward = diff + diffWeekDays;
  const shiftBackward = diff + diffWeekDays - 7;

  const overlappingDaysForward = getOverlappingDaysInIntervals(
    {
      start: scheduleStart,
      end: scheduleEnd,
    },
    {
      start: addDays(oldStartDate, shiftForward),
      end: addDays(oldEndDate, shiftForward),
    }
  );
  const overlappingDaysBackward = getOverlappingDaysInIntervals(
    {
      start: scheduleStart,
      end: scheduleEnd,
    },
    {
      start: addDays(oldStartDate, shiftBackward),
      end: addDays(oldEndDate, shiftBackward),
    }
  );

  let daysToShift = shiftForward;

  if (overlappingDaysBackward > overlappingDaysForward) {
    daysToShift = shiftBackward;
  }

  return (
    items
      // Update items date range
      .map((item) => {
        const itemStart = new Date(item.dateTimeRange.startDateTime);
        const itemEnd = new Date(item.dateTimeRange.endDateTime);

        let newStartDate = addDays(itemStart, daysToShift);
        let newEndDate = addDays(itemEnd, daysToShift);

        if (newStartDate.getTime() < scheduleStart.getTime() && newEndDate.getTime() > scheduleStart.getTime()) {
          // crop element's start date to prevent exceeding out of schedule range
          newStartDate = scheduleStart;
        } else if (
          newEndDate.getTime() > scheduleEndExtended.getTime() &&
          newStartDate.getTime() < scheduleEndExtended.getTime()
        ) {
          // crop element's end date to prevent exceeding out of schedule range
          newEndDate = scheduleEndExtended;
        }

        return {
          ...item,
          dateTimeRange: {
            startDateTime: newStartDate.toISOString(),
            endDateTime: newEndDate.toISOString(),
          },
        };
      })
      // Remove items that are not in new schedule date range
      .filter(
        ({ dateTimeRange: { startDateTime, endDateTime } }) =>
          isWithinInterval(new Date(startDateTime), { start: scheduleStart, end: scheduleEndExtended }) &&
          isWithinInterval(new Date(endDateTime), { start: scheduleStart, end: scheduleEndExtended })
      ) as ScheduleItemInput[]
  );
};
